import { InitGA } from '@bnnvara/analytics/src/components/GoogleAnalytics/InitGA';
import { track } from '@bnnvara/analytics/src/track';
import PropTypes from 'prop-types';
import React, { forwardRef, useRef, useState } from 'react';

import Bookmark from '../Bookmark';
import CommentButton from '../CommentButton';
import Cta from '../Cta';
import Icon from '../Icon';
import Share from '../Share';
import SliderHoc from '../Slider';
import TextBox from '../TextBox';
import * as S from './styled';

/**
 *
 * @param {function} callback
 */
const showTextBox = (callback) => {
  let timeOut;
  callback(true);
  // eslint-disable-next-line prefer-const
  timeOut = setTimeout(() => {
    callback(false);
    clearTimeout(timeOut);
  }, 2000);
};

const BRANDS_WITH_COMMENTS = [
  'kassa',
  'over mijn lijk',
  'oml',
  'vroege vogels tv',
  'vroege vogels',
];

const VideoPlayer = forwardRef(
  (
    {
      nodeId,
      loadingPosterImage,
      mediaId,
      detailPageUrl,
      duration,
      cta = undefined,
      player,
      userId,
      isFallback,
      brand,
    },
    ref
  ) => {
    const [shareIsActive, setShareIsActive] = useState(false);
    const [textBoxIsVisible, setTextBoxIsVisible] = useState(false);
    const [sliderIsVisible, setSliderIsVisible] = useState(false);
    const [ctaIsVisible, setCtaIsVisible] = useState(false);
    const copyTextBox = useRef();

    // check if video is embedded on brand-sites, if  not, do not show bookmark
    // This also means that the bookmark icon is not visible local
    const currentWindow = document.referrer;
    const atBNNVARA =
      currentWindow.includes('localhost') || currentWindow.includes('bnnvara');
    const isCommentable = BRANDS_WITH_COMMENTS.includes(brand?.toLowerCase());
    const isBookmarkable = atBNNVARA && userId !== 'null';
    const isSlidable = sliderIsVisible && !shareIsActive;
    const isCtaAllowed = ctaIsVisible && cta?.title;

    const toggleShareFn = ({ isCopiedToClipboard = false, isLink }) => {
      setShareIsActive(!shareIsActive);

      if (isCopiedToClipboard) {
        copyTextBox.current = isLink ? 'Link gekopiëerd' : 'Embed gekopiëerd';
        showTextBox(setTextBoxIsVisible);
      }

      // Ga track event
      track(
        'video-player-share',
        'clickToToggleShareModal',
        shareIsActive ? 'closes-share' : 'opens-share'
      );
    };
    const hideSlider = () => {
      setSliderIsVisible(false);
    };

    if (player) {
      // Play event listener (probably going to need this later)
      player.on('play', () => {
        setSliderIsVisible(false);
      });

      // Show CTA-button after 75% of the video
      player.on('timeupdate', () => {
        if (player.currentTime() >= duration * 0.75) {
          setCtaIsVisible(true);
        }
      });

      // Pause event
      player.on('pause', () => {
        setSliderIsVisible(true);
      });
    }

    if (isFallback) {
      return (
        <>
          <InitGA id={process.env.GA_ID_PLAYER} />
          <S.Video
            ref={ref}
            id={nodeId}
            poster={loadingPosterImage}
            preload="metadata"
            className="video-js"
            playsInline
          />
        </>
      );
    }

    return (
      <>
        <InitGA id={process.env.GA_ID_PLAYER} />
        <S.TopIcons className="js-top-icons">
          {isCommentable && <CommentButton detailPageUrl={detailPageUrl} />}
          {isBookmarkable && <Bookmark mediaId={mediaId} userId={userId} />}
          <S.ShareToggleButton type="button" onClick={toggleShareFn}>
            <Icon icon="share" size="25" color="#fff" />
            {textBoxIsVisible && <TextBox>{copyTextBox.current}</TextBox>}
          </S.ShareToggleButton>
        </S.TopIcons>
        {isCtaAllowed && (
          <Cta
            as="a"
            href={cta?.url || null}
            target="_blank"
            rel="noopener"
            onClick={() => track('video-player-cta', 'click', cta?.url)}
          >
            {cta.title}
          </Cta>
        )}
        <S.Video
          ref={ref}
          id={nodeId}
          poster={loadingPosterImage}
          preload="metadata"
          className="video-js"
          playsInline
        />
        {shareIsActive && (
          <Share
            toggleShareFn={toggleShareFn}
            mediaId={mediaId}
            detailPageUrl={detailPageUrl}
          />
        )}
        {isSlidable && <SliderHoc hideSlider={hideSlider} id={mediaId} />}
      </>
    );
  }
);

VideoPlayer.propTypes = {
  nodeId: PropTypes.string,
  mediaId: PropTypes.string,
  loadingPosterImage: PropTypes.string.isRequired,
  detailPageUrl: PropTypes.string,
  duration: PropTypes.number,
  cta: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  userId: PropTypes.string,
  // TODO find a better solution for the player prop it's to big to be past down. Hint: look for examples of react videojs comps and see how its done there
  // eslint-disable-next-line react/forbid-prop-types
  player: PropTypes.any,
  isFallback: PropTypes.bool,
  brand: PropTypes.string,
};

export default VideoPlayer;
